import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Utensils, Sprout, Search, X, Clock, Users } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { plannerAPI, recipePhotoUrl } from "@/lib/planner";
import { toast } from "sonner";
import QuickPlantRecipeDialog from "@/components/planner/QuickPlantRecipeDialog";

/**
 * MealPicker — Instagram-style browse dialog for pinning a Grove recipe to
 * a meal-plan day. A large modal with a live search input up top and a
 * responsive photo grid below, split into two sections:
 *   1. Your recipes (recipes you planted, sorted newest first)
 *   2. From the Grove (everyone else's meal-plannable recipes)
 * The "Plant a new recipe" card sits at the very top of the grid so a user
 * with zero recipes (or one that isn't in the Grove yet) can create one
 * inline without leaving the flow — it becomes a real Grove seed and shows
 * up on their Roots profile afterwards.
 *
 * Props:
 *   open        — bool
 *   onClose     — () => void
 *   date        — target date "YYYY-MM-DD" (required when open)
 *   onSaved     — () => void; fired after a successful add so the caller
 *                 can refresh its meal-plan data
 *   replaceEntryId — optional string; if present we delete this existing
 *                    meal entry before adding the new pick (used for
 *                    "change meal" flow on already-planned days)
 */
export default function MealPicker({ open, onClose, date, mealType = "dinner", onSaved, replaceEntryId }) {
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [quickPlantOpen, setQuickPlantOpen] = useState(false);
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setQuery("");
    plannerAPI
      .eligibleRecipes()
      .then((d) => setRecipes(d.recipes || []))
      .catch(() => toast.error("Couldn't load your recipes."))
      .finally(() => setLoading(false));
  }, [open]);

  const choose = async (idea_id) => {
    try {
      if (replaceEntryId) {
        try { await plannerAPI.deleteMeal(replaceEntryId); } catch { /* fall through */ }
      }
      await plannerAPI.addMeal({ date, idea_id, meal_type: mealType });
      toast.success(replaceEntryId ? "Meal updated." : "Added to your meal plan.");
      onSaved?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't add that meal.");
    }
  };

  const dateLabel = date
    ? new Date(date + "T00:00:00").toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" })
    : "";

  // Client-side filter across title + author. Cheap enough for the current
  // scale (single-user library + Grove size). If this ever gets slow we'll
  // move filtering server-side via a `q=` param on /eligible-recipes.
  const { mine, grove } = useMemo(() => {
    const q = query.trim().toLowerCase();
    const match = (r) => !q || (r.title || "").toLowerCase().includes(q) || (r.author_name || "").toLowerCase().includes(q);
    const mine = recipes.filter((r) => r.source === "yours" && match(r));
    const grove = recipes.filter((r) => r.source === "grove" && match(r));
    return { mine, grove };
  }, [recipes, query]);

  const totalMatches = mine.length + grove.length;

  return (
    <>
      <Dialog open={open && !quickPlantOpen} onOpenChange={(o) => !o && onClose?.()}>
        <DialogContent
          className="max-w-4xl bg-[#F7F3ED] border-[#A87F56]/30 max-h-[90vh] p-0 gap-0 overflow-hidden flex flex-col"
          data-testid="planner-meal-picker"
        >
          {/* Sticky header: title + search */}
          <div className="px-6 pt-6 pb-4 border-b border-[#A87F56]/20 shrink-0">
            <DialogHeader className="mb-3">
              <DialogTitle
                className="text-2xl text-[#2A2823]"
                style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
              >
                {replaceEntryId ? "Swap the recipe" : `Pick a ${mealType} recipe`}
                <span className="text-[#2A2823]/50 font-normal text-lg"> · {dateLabel}</span>
              </DialogTitle>
            </DialogHeader>
            <div className="relative">
              <Search className="w-4 h-4 text-[#2A2823]/40 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search the Grove — try 'salmon', 'weeknight', a friend's name…"
                className="w-full h-11 pl-10 pr-10 rounded-full border border-[#A87F56]/30 bg-white text-sm text-[#2A2823] placeholder:text-[#2A2823]/40 focus:outline-none focus:ring-2 focus:ring-[#3E4A37]/30"
                data-testid="planner-meal-picker-search"
                autoFocus
              />
              {query && (
                <button
                  onClick={() => setQuery("")}
                  aria-label="Clear search"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#2A2823]/40 hover:text-[#2A2823]"
                  data-testid="planner-meal-picker-clear-search"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            {query && !loading && (
              <div className="mt-2 text-xs text-[#2A2823]/55" data-testid="planner-meal-picker-match-count">
                {totalMatches} {totalMatches === 1 ? "match" : "matches"} for &ldquo;{query}&rdquo;
              </div>
            )}
          </div>

          {/* Scrollable grid */}
          <div className="flex-1 overflow-y-auto px-6 py-5">
            {loading ? (
              <div className="py-10 text-center text-sm text-[#2A2823]/50">Loading recipes…</div>
            ) : (
              <div className="space-y-6">
                {/* Plant-a-new-recipe CTA tile — sits at the very top of the
                    grid so a first-time user (or someone with a recipe not
                    yet in the Grove) can create one without leaving. */}
                <SectionGrid label="Add fresh">
                  <button
                    onClick={() => setQuickPlantOpen(true)}
                    data-testid="planner-meal-picker-create-new"
                    className="group aspect-[4/5] rounded-2xl border border-dashed border-[#A87F56]/50 bg-white/60 hover:bg-white flex flex-col items-center justify-center p-4 transition"
                  >
                    <div className="w-14 h-14 rounded-full bg-[#EDE5DA] flex items-center justify-center mb-3 group-hover:bg-[#A87F56]/20 transition">
                      <Sprout className="w-6 h-6 text-[#3E4A37]" strokeWidth={1.6} />
                    </div>
                    <div
                      className="text-lg text-[#2A2823] text-center leading-tight"
                      style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
                    >
                      Plant a new recipe
                    </div>
                    <div className="text-xs text-[#2A2823]/55 mt-1.5 text-center">
                      Add one now — it lives in your Grove and on your Roots profile.
                    </div>
                  </button>
                </SectionGrid>

                {mine.length > 0 && (
                  <SectionGrid label="Your recipes" testid="planner-meal-picker-section-mine">
                    {mine.map((r) => (
                      <RecipeTile key={r.id} recipe={r} onPick={() => choose(r.id)} />
                    ))}
                  </SectionGrid>
                )}

                {grove.length > 0 && (
                  <SectionGrid label="From the Grove" testid="planner-meal-picker-section-grove">
                    {grove.map((r) => (
                      <RecipeTile key={r.id} recipe={r} onPick={() => choose(r.id)} showAuthor />
                    ))}
                  </SectionGrid>
                )}

                {totalMatches === 0 && (
                  <div className="py-10 text-center text-sm text-[#2A2823]/60">
                    {query ? (
                      <>
                        <p>Nothing matches &ldquo;{query}&rdquo; yet.</p>
                        <p className="mt-2 text-xs text-[#2A2823]/45">
                          Try another word — or plant a fresh one above.
                        </p>
                      </>
                    ) : (
                      <>
                        <p>The Grove doesn't have any meal-plannable recipes yet.</p>
                        <p className="mt-2 text-xs text-[#2A2823]/45">
                          Recipes need at least one ingredient to be meal-planned. Plant one above, or open the{" "}
                          <Link to="/plant?category=Recipes" className="underline underline-offset-2 text-[#3E4A37]">
                            full recipe planter
                          </Link>{" "}
                          for steps, timers, and extra photos.
                        </p>
                      </>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <QuickPlantRecipeDialog
        open={quickPlantOpen}
        onClose={() => setQuickPlantOpen(false)}
        targetDateLabel={dateLabel}
        onCreated={async (idea) => {
          setQuickPlantOpen(false);
          if (idea?.id) await choose(idea.id);
        }}
      />
    </>
  );
}

function SectionGrid({ label, testid, children }) {
  return (
    <section data-testid={testid}>
      <div className="text-[10px] tracking-[0.35em] text-[#A87F56] mb-3">{label.toUpperCase()}</div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        {children}
      </div>
    </section>
  );
}

function RecipeTile({ recipe, onPick, showAuthor = false }) {
  const meta = [];
  if (recipe.servings) meta.push({ icon: Users, text: `Serves ${recipe.servings}` });
  if (recipe.total_time) meta.push({ icon: Clock, text: recipe.total_time });
  // Recipes without ingredient rows can still be added to the meal plan —
  // they simply contribute nothing to the auto shopping list. A small
  // muted hint on the tile so cooks aren't surprised.
  const noIngredients = recipe.meal_plannable === false;
  return (
    <button
      onClick={onPick}
      data-testid={`planner-meal-picker-choice-${recipe.id}`}
      className="group text-left rounded-2xl overflow-hidden bg-white border border-[#A87F56]/20 hover:border-[#A87F56]/60 hover:shadow-md transition focus:outline-none focus:ring-2 focus:ring-[#3E4A37]/40"
    >
      <div className="aspect-square bg-[#EDE5DA] relative overflow-hidden">
        {recipe.photo_path ? (
          <img
            src={recipePhotoUrl(recipe.photo_path)}
            alt={recipe.title}
            className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-300"
            onError={(e) => { e.currentTarget.style.display = "none"; }}
            draggable={false}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Utensils className="w-6 h-6 text-[#3E4A37]/40" />
          </div>
        )}
        {showAuthor && recipe.author_name && (
          <div className="absolute bottom-2 left-2 right-2 bg-black/45 backdrop-blur-sm text-white text-[10px] tracking-wide px-2 py-1 rounded-md truncate">
            by {recipe.author_name}
          </div>
        )}
      </div>
      <div className="p-2.5">
        <div className="text-[13px] leading-tight text-[#2A2823] line-clamp-2 min-h-[2.4em]">
          {recipe.title}
        </div>
        {meta.length > 0 && (
          <div className="mt-1.5 flex items-center gap-2 text-[11px] text-[#2A2823]/55">
            {meta.map(({ icon: Icon, text }, i) => (
              <span key={i} className="inline-flex items-center gap-1">
                <Icon className="w-3 h-3" strokeWidth={1.6} />
                {text}
              </span>
            ))}
          </div>
        )}
        {noIngredients && (
          <div
            className="mt-1.5 text-[10px] text-[#A87F56] italic leading-tight"
            data-testid={`planner-meal-picker-no-ings-${recipe.id}`}
            title="Structured ingredient rows are separate from anything typed in the recipe's story. Add them from the recipe's Edit page."
          >
            No structured ingredients — you can still plan it, shopping list stays empty.
          </div>
        )}
      </div>
    </button>
  );
}
